import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { clientService, ConfirmationComparisonFieldsSettings as SettingsType } from '../../services/clientService';
import { useAuth } from '../auth/AuthContext';
import './ConfirmationComparisonFieldsSettings.css';

/**
 * Field definition for the settings panel
 */
interface FieldDefinition {
  name: string;
  displayKey: string; // i18n key under grid.columns.*
  comparable: boolean;
  forwardOnly?: boolean;
  asiaticOnly?: boolean;
  forwardStartingOnly?: boolean;
}

interface Bank {
  id: string;
  name: string;
  isCounterparty?: boolean;
}

interface Props {
  availableBanks: Bank[];
}

/**
 * All comparable fields for spot trades
 */
const SPOT_COMPARABLE_FIELDS: FieldDefinition[] = [
  { name: 'OurParty', displayKey: 'entity', comparable: true },
  { name: 'CounterpartyName', displayKey: 'counterparty', comparable: true },
  { name: 'ProductType', displayKey: 'productType', comparable: true },
  { name: 'TradeDate', displayKey: 'tradeDate', comparable: true },
  { name: 'ValueDate', displayKey: 'valueDate', comparable: true },
  { name: 'Direction', displayKey: 'direction', comparable: true },
  { name: 'Currency1', displayKey: 'currency1', comparable: true },
  { name: 'QuantityCurrency1', displayKey: 'amount', comparable: true },
  { name: 'Price', displayKey: 'price', comparable: true },
  { name: 'Currency2', displayKey: 'currency2', comparable: true },
  { name: 'QuantityCurrency2', displayKey: 'contraAmount', comparable: true },
  { name: 'Currency1PaymentDate', displayKey: 'currency1PaymentDate', comparable: true },
  { name: 'Currency2PaymentDate', displayKey: 'currency2PaymentDate', comparable: true },
];

/**
 * Additional comparable fields for forward trades
 */
const FORWARD_ADDITIONAL_FIELDS: FieldDefinition[] = [
  { name: 'MaturityDate', displayKey: 'maturityDate', comparable: true, forwardOnly: true },
  { name: 'FixingReference', displayKey: 'fixingReference', comparable: true, forwardOnly: true },
  { name: 'FixingOffset', displayKey: 'fixingOffset', comparable: true, forwardOnly: true },
  { name: 'AverageRateFixingDates', displayKey: 'fixingDates', comparable: true, forwardOnly: true, asiaticOnly: true },
  { name: 'ForwardPoints', displayKey: 'forwardPoints', comparable: true, forwardOnly: true, forwardStartingOnly: true },
  { name: 'SpotFixingReference', displayKey: 'spotFixingReference', comparable: true, forwardOnly: true, forwardStartingOnly: true },
  { name: 'ForwardStartingFixingDates', displayKey: 'forwardStartingFixingDates', comparable: true, forwardOnly: true, forwardStartingOnly: true },
  { name: 'SettlementType', displayKey: 'settlementType', comparable: true, forwardOnly: true },
  { name: 'SettlementCurrency', displayKey: 'settlementCurrency', comparable: true, forwardOnly: true },
  { name: 'PaymentDate', displayKey: 'paymentDate', comparable: true, forwardOnly: true },
];

type TabType = 'default' | 'counterparty';

const ConfirmationComparisonFieldsSettings: React.FC<Props> = ({ availableBanks }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('default');

  // Selected counterparty for Per Counterparty tab
  const [selectedCounterpartyId, setSelectedCounterpartyId] = useState<string | null>(null);

  // Load settings on mount
  useEffect(() => {
    if (clientId) {
      loadSettings();
    }
  }, [clientId]);

  const loadSettings = async () => {
    if (!clientId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await clientService.getConfirmationComparisonFields(clientId);
      if (response.success && response.data) {
        setSettings(response.data);
      } else {
        setError(response.message || t('confirmations.comparisonFields.error'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('confirmations.comparisonFields.error'));
    } finally {
      setLoading(false);
    }
  };

  // Get the config key based on tab and selection
  const configKey = activeTab === 'default' ? '_default' : selectedCounterpartyId;

  // Get current config for selected context
  const currentConfig = useMemo(() => {
    if (!configKey) return null;
    return settings?.counterpartyFields?.[configKey] || { spot: [], forward: [] };
  }, [settings, configKey]);

  // Get default config for "copy from default" functionality
  const defaultConfig = useMemo(() => {
    return settings?.counterpartyFields?.['_default'] || { spot: [], forward: [] };
  }, [settings]);

  // Get list of counterparties that have custom configs
  const counterpartiesWithConfig = useMemo(() => {
    if (!settings?.counterpartyFields) return [];
    return Object.keys(settings.counterpartyFields).filter(k => k !== '_default');
  }, [settings]);

  // Check if a field is selected for a product type
  const isFieldSelected = (fieldName: string, productType: 'spot' | 'forward'): boolean => {
    if (!currentConfig) return false;
    const fields = productType === 'spot' ? currentConfig.spot : currentConfig.forward;
    return fields.includes(fieldName);
  };

  // Toggle a field selection
  const toggleField = (fieldName: string, productType: 'spot' | 'forward') => {
    if (!settings || !configKey) return;

    const config = currentConfig || { spot: [], forward: [] };
    const newConfig = { ...config };
    const fields = productType === 'spot' ? [...newConfig.spot] : [...newConfig.forward];

    const index = fields.indexOf(fieldName);
    if (index >= 0) {
      fields.splice(index, 1);
    } else {
      fields.push(fieldName);
    }

    if (productType === 'spot') {
      newConfig.spot = fields;
    } else {
      newConfig.forward = fields;
    }

    setSettings({
      ...settings,
      counterpartyFields: {
        ...settings.counterpartyFields,
        [configKey]: newConfig
      }
    });
    setHasChanges(true);
  };

  // Select/deselect all fields for a product type
  const toggleAllFields = (productType: 'spot' | 'forward', select: boolean) => {
    if (!settings || !configKey) return;

    const fields = productType === 'spot'
      ? SPOT_COMPARABLE_FIELDS.map(f => f.name)
      : [...SPOT_COMPARABLE_FIELDS, ...FORWARD_ADDITIONAL_FIELDS].map(f => f.name);

    const config = currentConfig || { spot: [], forward: [] };
    const newConfig = { ...config };
    if (productType === 'spot') {
      newConfig.spot = select ? fields : [];
    } else {
      newConfig.forward = select ? fields : [];
    }

    setSettings({
      ...settings,
      counterpartyFields: {
        ...settings.counterpartyFields,
        [configKey]: newConfig
      }
    });
    setHasChanges(true);
  };

  // Handle counterparty selection
  const handleCounterpartySelect = (counterpartyId: string | null) => {
    setSelectedCounterpartyId(counterpartyId);
  };

  // Copy from default configuration
  const copyFromDefault = () => {
    if (!settings || !selectedCounterpartyId) return;

    setSettings({
      ...settings,
      counterpartyFields: {
        ...settings.counterpartyFields,
        [selectedCounterpartyId]: { ...defaultConfig }
      }
    });
    setHasChanges(true);
  };

  // Remove counterparty-specific config (revert to using default)
  const removeCounterpartyConfig = () => {
    if (!settings || !selectedCounterpartyId) return;

    const newCounterpartyFields = { ...settings.counterpartyFields };
    delete newCounterpartyFields[selectedCounterpartyId];

    setSettings({
      ...settings,
      counterpartyFields: newCounterpartyFields
    });
    setHasChanges(true);
    setSelectedCounterpartyId(null);
  };

  // Save settings
  const handleSave = async () => {
    if (!clientId || !settings) return;

    setSaving(true);
    setError(null);
    try {
      const response = await clientService.updateConfirmationComparisonFields(clientId, {
        counterpartyFields: settings.counterpartyFields
      });
      if (response.success) {
        setSettings(response.data);
        setHasChanges(false);
      } else {
        setError(response.message || t('confirmations.comparisonFields.saveError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('confirmations.comparisonFields.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Reset to last saved state
  const handleReset = () => {
    loadSettings();
    setHasChanges(false);
  };

  // Render field toggle row
  const renderFieldToggle = (field: FieldDefinition, productType: 'spot' | 'forward') => {
    const isSelected = isFieldSelected(field.name, productType);
    const fieldLabel = t(`grid.columns.${field.displayKey}`);

    return (
      <div key={`${productType}-${field.name}`} className="field-toggle-row">
        <span className="field-name" title={fieldLabel}>{fieldLabel}</span>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleField(field.name, productType)}
          />
          <span className="slider"></span>
        </label>
      </div>
    );
  };

  // Render fields section (spot or forward)
  const renderFieldsSection = (productType: 'spot' | 'forward') => {
    const titleKey = productType === 'spot' ? 'spotFields' : 'forwardFields';
    const fields = productType === 'spot'
      ? SPOT_COMPARABLE_FIELDS
      : [...SPOT_COMPARABLE_FIELDS, ...FORWARD_ADDITIONAL_FIELDS];

    // Split into regular, Asiático-only, and Forward Starting-only fields
    const regularFields = fields.filter(f => !f.asiaticOnly && !f.forwardStartingOnly);
    const asiaticFields = fields.filter(f => f.asiaticOnly);
    const forwardStartingFields = fields.filter(f => f.forwardStartingOnly);

    return (
      <div className="fields-section">
        <div className="fields-section-header">
          <h4>{t(`confirmations.comparisonFields.${titleKey}`)}</h4>
          <div className="section-actions">
            <button type="button" className="link-button" onClick={() => toggleAllFields(productType, true)}>
              {t('confirmations.comparisonFields.selectAll')}
            </button>
            <span className="separator">|</span>
            <button type="button" className="link-button" onClick={() => toggleAllFields(productType, false)}>
              {t('confirmations.comparisonFields.deselectAll')}
            </button>
          </div>
        </div>
        <div className="fields-toggle-grid">
          {regularFields.map(field => renderFieldToggle(field, productType))}
        </div>
        {asiaticFields.length > 0 && (
          <>
            <div className="fields-subsection-header">
              <span>{t('confirmations.comparisonFields.asiaticOnlyFields')}</span>
            </div>
            <div className="fields-toggle-grid">
              {asiaticFields.map(field => renderFieldToggle(field, productType))}
            </div>
          </>
        )}
        {forwardStartingFields.length > 0 && (
          <>
            <div className="fields-subsection-header">
              <span>{t('confirmations.comparisonFields.forwardStartingOnlyFields')}</span>
            </div>
            <div className="fields-toggle-grid">
              {forwardStartingFields.map(field => renderFieldToggle(field, productType))}
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="comparison-fields-settings">
        <div className="settings-header">
          <h3>{t('confirmations.comparisonFields.title')}</h3>
        </div>
        <div className="settings-loading">
          <div className="spinner" />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="comparison-fields-settings">
      <div className="settings-header">
        <h3>{t('confirmations.comparisonFields.title')}</h3>
        <p>{t('confirmations.comparisonFields.description')}</p>
      </div>

      {error && (
        <div className="settings-error">
          <p>{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="config-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'default' ? 'active' : ''}`}
          onClick={() => setActiveTab('default')}
        >
          {t('confirmations.comparisonFields.defaultTab')}
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'counterparty' ? 'active' : ''}`}
          onClick={() => setActiveTab('counterparty')}
        >
          {t('confirmations.comparisonFields.counterpartyTab')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'default' && (
          <div className="fields-container">
            {renderFieldsSection('spot')}
            {renderFieldsSection('forward')}
          </div>
        )}

        {activeTab === 'counterparty' && (
          <div className="counterparty-content">
            {/* Counterparty selector */}
            <div className="counterparty-selector">
              <select
                value={selectedCounterpartyId || ''}
                onChange={(e) => handleCounterpartySelect(e.target.value || null)}
              >
                <option value="">{t('confirmations.comparisonFields.selectCounterparty')}</option>
                {availableBanks.filter(bank => bank.isCounterparty !== false).map(bank => (
                  <option key={bank.id} value={bank.id}>
                    {bank.name}
                    {counterpartiesWithConfig.includes(bank.id) ? ' *' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Counterparty config content */}
            {selectedCounterpartyId ? (
              counterpartiesWithConfig.includes(selectedCounterpartyId) ? (
                <>
                  {/* Action buttons for existing config */}
                  <div className="counterparty-actions">
                    <button
                      type="button"
                      className="link-button"
                      onClick={copyFromDefault}
                    >
                      {t('confirmations.comparisonFields.copyFromDefault')}
                    </button>
                    <span className="separator">|</span>
                    <button
                      type="button"
                      className="link-button danger"
                      onClick={removeCounterpartyConfig}
                      title={t('confirmations.comparisonFields.removeConfig')}
                    >
                      🗑️
                    </button>
                  </div>

                  <div className="fields-container">
                    {renderFieldsSection('spot')}
                    {renderFieldsSection('forward')}
                  </div>
                </>
              ) : (
                /* No config exists for this counterparty - show empty state with copy button */
                <div className="empty-state">
                  <p>{t('confirmations.comparisonFields.noConfigForCounterparty')}</p>
                  <button
                    type="button"
                    className="link-button"
                    onClick={copyFromDefault}
                  >
                    {t('confirmations.comparisonFields.copyFromDefault')}
                  </button>
                </div>
              )
            ) : (
              <div className="empty-state">
                <p>{t('confirmations.comparisonFields.noCounterpartySelected')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons - matching Admin Dashboard style */}
      <div className="settings-actions">
        <button
          onClick={handleReset}
          disabled={!hasChanges || saving}
          className="reset-button secondary"
        >
          {t('common.reset')}
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="save-button primary"
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </div>
  );
};

export default ConfirmationComparisonFieldsSettings;
